import type { SyncElectronApi } from './SyncBridgeTypes';
import type { SyncOutboxPort } from './SyncPorts';

export class SyncBridgeUnavailableError extends Error {
  constructor() {
    super('Synchronisation indisponible hors de l’application de bureau.');
    this.name = 'SyncBridgeUnavailableError';
  }
}

type PartialApi = Partial<SyncElectronApi>;

function requireApi(api: PartialApi | undefined): SyncElectronApi {
  if (
    !api ||
    !api.syncClaimDue ||
    !api.syncAck ||
    !api.syncApplyRemoteBatch ||
    !api.syncGetState ||
    !api.syncMarkRetry ||
    !api.syncMarkRejected ||
    !api.syncResetSending ||
    !api.syncUpdateMeta ||
    !api.syncLocalCounts ||
    !api.syncEnqueueAllLocal ||
    !api.syncInitialMergeBatch ||
    !api.syncInitialMergeFinish ||
    !api.syncBeginRebootstrap ||
    !api.syncReconcileSnapshot ||
    !api.syncListAttention ||
    !api.syncResolveAttention ||
    !api.syncRecordEvent ||
    !api.syncEnable ||
    !api.syncDisable ||
    !api.syncSetAuthor ||
    !api.syncExportDiagnostic
  ) {
    throw new SyncBridgeUnavailableError();
  }
  return api as SyncElectronApi;
}

/** Vrai si l'application de bureau expose la file de synchronisation. */
export function isSyncBridgeAvailable(api: PartialApi | undefined = window.electron): boolean {
  try {
    requireApi(api);
    return true;
  } catch {
    return false;
  }
}

/**
 * File d'envoi du processus principal, liée à UNE société. Toutes les méthodes passent par l'IPC fermé de
 * `electron/sync-ipc.cjs`, qui refuse tout appel dont la société n'est pas celle de la session.
 */
export function createElectronSyncOutbox(
  tenantId: string,
  api: PartialApi | undefined = window.electron,
): SyncOutboxPort {
  const bridge = requireApi(api);
  return {
    getState: () => bridge.syncGetState(tenantId),
    updateMeta: (patch) => bridge.syncUpdateMeta(tenantId, patch),
    claimDue: (max, maxBytes) => bridge.syncClaimDue(tenantId, max, maxBytes),
    resetSending: () => bridge.syncResetSending(),
    ack: (results) => bridge.syncAck(tenantId, results),
    markRetry: (items, code, countAttempt) => bridge.syncMarkRetry(tenantId, items, code, countAttempt),
    markRejected: (opId, code, message, entity) => bridge.syncMarkRejected(tenantId, opId, code, message, entity),
    applyRemoteBatch: (items, opts) => bridge.syncApplyRemoteBatch(tenantId, items, opts),
    localCounts: () => bridge.syncLocalCounts(tenantId),
    enqueueAllLocal: () => bridge.syncEnqueueAllLocal(tenantId),
    initialMergeBatch: (items, strategy) => bridge.syncInitialMergeBatch(tenantId, items, strategy),
    initialMergeFinish: (cursor, epoch) => bridge.syncInitialMergeFinish(tenantId, cursor, epoch),
    beginRebootstrap: (mode) => bridge.syncBeginRebootstrap(tenantId, mode),
    reconcileSnapshot: (keys, mode, resources) => bridge.syncReconcileSnapshot(tenantId, keys, mode, resources),
    listAttention: () => bridge.syncListAttention(tenantId),
    resolveAttention: (id, action) => bridge.syncResolveAttention(tenantId, id, action),
    recordEvent: (event) => bridge.syncRecordEvent({ ...event, tenantId }),
  };
}

/** Actions administratives (consentement) sur la société de la session. */
export function createElectronSyncAdmin(tenantId: string, api: PartialApi | undefined = window.electron) {
  const bridge = requireApi(api);
  return {
    enable: (consentVersion: string) => bridge.syncEnable(tenantId, consentVersion),
    disable: () => bridge.syncDisable(tenantId),
    setAuthor: (userId: string | null) => bridge.syncSetAuthor(tenantId, userId),
    exportDiagnostic: () => bridge.syncExportDiagnostic(tenantId),
    getState: () => bridge.syncGetState(tenantId),
  };
}
